// src/Pages/Login.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button"; 
import { ChevronRight, Eye, EyeOff } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import LanguageSelector from '../Components/Layout/LanguageSelector';
import logo from '../images/pslogo.png';

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [fieldErrors, setFieldErrors] = useState({
        email: '',
        password: ''
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim()) {
            return t('validation.emailRequired');
        }
        if (!emailRegex.test(email)) {
            return t('validation.invalidEmail');
        }
        return '';
    };

    const validatePassword = (password) => {
        if (!password) {
            return t('validation.passwordRequired');
        }
        if (password.length < 8) {
            return t('validation.passwordMinLength');
        }
        return '';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear general error when user types
        if (error) {
            setError('');
        }

        // Clear field-specific error when user types
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        let fieldError = '';

        if (name === 'email') {
            fieldError = validateEmail(value);
        } else if (name === 'password') {
            fieldError = validatePassword(value);
        }

        setFieldErrors(prev => ({
            ...prev,
            [name]: fieldError
        }));
    };

    const validateForm = () => {
        const emailError = validateEmail(formData.email);
        const passwordError = validatePassword(formData.password);

        setFieldErrors({
            email: emailError,
            password: passwordError
        });

        return !emailError && !passwordError;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/login', formData);

            if (response.data && response.data.accesstoken) {
                localStorage.setItem('token', response.data.accesstoken);
                localStorage.setItem('refreshToken', response.data.refreshtoken);
                localStorage.setItem('expiresIn', response.data.expiresin);

                if (response.data.menu) {
                    localStorage.setItem('selectedMenu', response.data.menu.id);
                    localStorage.setItem('selectedMenuName', response.data.menu.name);
                }

                navigate('/dashboard');
            } else {
                setError(t('auth.loginError') || 'Er ging iets mis bij het inloggen.');
            }
        } catch (err) {
            console.log('Login error:', err);

            if (err.response?.status === 401) {
                setError(t('auth.invalidCredentials') || 'Ongeldige email of wachtwoord.');
            } else if (err.response?.status === 423) {
                setError(t('auth.accountLocked') || 'Account is vergrendeld. Neem contact op met de beheerder.');
            } else if (err.response?.status === 403) {
                setError(t('auth.emailNotVerified') || 'E-mailadres is nog niet geverifieerd.');
            } else if (err.request) {
                setError(t('auth.connectionError') || 'Kan geen verbinding maken met de server.');
            } else {
                setError(t('auth.loginError') || 'Er ging iets mis bij het inloggen.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4">
            <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg relative">
                {/* Logo */}
                <div className="absolute left-[15px] top-[-5px] w-[80px]">
                    <img src={logo} alt="PS Logo" />
                </div>
                
                {/* Language Selector */}
                <div className="absolute right-4 top-4">
                    <LanguageSelector />
                </div>

                {/* Header Section */}
                <div className="text-center pt-8">
                    <h1 className="text-3xl font-inter text-ps-blue-dark mb-2">
                        PS Menu Maker
                    </h1>
                    <p className="text-sm text-gray-600 font-inter">
                        {t('auth.loginSubtitle') || 'Log in om verder te gaan'}
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    {/* General Error Display */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg font-inter">
                            <div className="flex items-start">
                                <div className="flex-1">{error}</div>
                            </div>
                        </div>
                    )}

                    {/* Email Field */}
                    <div>
                        <label 
                            htmlFor="email" 
                            className="block text-sm font-inter text-ps-blue-dark mb-2"
                        >
                            {t('auth.email')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="email"
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                fieldErrors.email 
                                    ? 'border-red-500 focus:border-red-500' 
                                    : 'border-gray-300 focus:border-ps-blue-dark'
                            }`}
                            placeholder={t('auth.emailPlaceholder') || 'naam@bedrijf.nl'}
                            disabled={isLoading}
                            autoComplete="email"
                        />
                        {fieldErrors.email && (
                            <p className="text-red-500 text-sm mt-1 font-inter">
                                {fieldErrors.email}
                            </p>
                        )}
                    </div>

                    {/* Password Field */}
                    <div>
                        <label 
                            htmlFor="password" 
                            className="block text-sm font-inter text-ps-blue-dark mb-2"
                        >
                            {t('auth.password')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 border rounded-md bg-white font-inter pr-12 transition-colors ${
                                    fieldErrors.password 
                                        ? 'border-red-500 focus:border-red-500' 
                                        : 'border-gray-300 focus:border-ps-blue-dark'
                                }`}
                                placeholder="••••••••"
                                disabled={isLoading}
                                autoComplete="current-password"
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                disabled={isLoading}
                                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {fieldErrors.password && (
                            <p className="text-red-500 text-sm mt-1 font-inter">
                                {fieldErrors.password}
                            </p>
                        )}
                    </div>

                    {/* Forgot Password Link */}
                    <div className="flex items-center justify-between">
                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/forgot-password')}
                            className="text-ps-blue-dark hover:text-ps-blue text-sm font-inter p-0 h-auto"
                            disabled={isLoading}
                        >
                            {t('auth.forgotPassword')}
                        </Button>
                    </div>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        disabled={isLoading || !formData.email || !formData.password}
                        className="w-full bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white flex items-center justify-center gap-2 h-12 font-inter transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {t('auth.signingIn') || 'Bezig met inloggen...'}
                            </>
                        ) : (
                            <>
                                {t('auth.login')}
                                <ChevronRight className="h-4 w-4" />
                            </>
                        )}
                    </Button>

                    {/* Register Link */}
                    <div className="text-center">
                        <p className="text-sm text-gray-600 font-inter">
                            {t('auth.noAccount') || 'Nog geen account?'}{' '}
                            <Button
                                type="button"
                                variant="link"
                                onClick={() => navigate('/register')}
                                className="text-ps-blue-dark hover:text-ps-blue font-medium p-0 h-auto"
                                disabled={isLoading}
                            >
                                {t('auth.registerHere') || 'Registreer hier'}
                            </Button>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;